import React, { useEffect, useState } from "react";
import Action from "../../../components/common/Action";
import DebounceSearch from "../../../components/common/DebounceSearch";
import CustomTable from "../../../components/customtable/CustomTable";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../../components/common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import {
  deleteRecipe,
  getRecipeList,
} from "../../../redux/nutrition-recipes/slice";
import RippleLoader from "../../../components/RippleLoader/RippleLoader";
import { SortDownArrow } from "../../../assets/svg";

const NutritionRecipes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [recipeDelete, setRecipeDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);

  // selector
  const { allRecipesList, allRecipesLoading } = useSelector((store) => ({
    allRecipesList: store?.recipeData?.getRecipeListStatus?.data,
    allRecipesLoading: store?.recipeData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getRecipeList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        limit: pageLimit,
      }),
    );
  }, [searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  const deleteRecipeId = async () => {
    setIsLoading(true);
    if (!recipeDelete) return;

    try {
      await dispatch(deleteRecipe(recipeDelete)).unwrap();
      setIsLoading(false);
      setConfirmDelete(false);

      dispatch(
        getRecipeList({
          search: searchValue,
          pageCount,
          pageLimit,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: pageLimit,
        }),
      );
    } catch (err) {
      console.error("Failed to delete recipe:", err);
    }
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-16 table-th">No.</th>
      <th className="w-20 table-th">
        <span>Image</span>
      </th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("recipeNames");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "recipeNames"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("nutritionListName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Nutritionist Name </span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "nutritionListName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">Details</th>
      <th className="table-th">Ingredients</th>
      <th className="w-24 table-th">Actions</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(allRecipesList?.data?.data || []).map((item, index) => (
        <tr key={item._id} className="shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <img
              src={item?.recipeImage}
              alt="Recipe"
              className="w-10 h-10 object-cover"
            />
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-2" title={item?.recipeName}>
              {item?.recipeName || "-"}
            </div>
          </td>
          <td className="px-5 py-3">{item?.nutritionListName || "-"}</td>
          <td className="px-5 py-3 text-start truncate" title={item?.details}>
            {item?.details || "-"}
          </td>
          <td className="px-5 py-3 text-start truncate">
            <div
              className="truncate"
              title={(item.ingredients || [])
                .map((ingredient) => ingredient.ingredientName)
                .join(", ")}
            >
              {(item.ingredients || []).map((ingredient, i) => (
                <span key={ingredient._id}>
                  {ingredient.ingredientName}
                  {i !== item.ingredients.length - 1 && ", "}
                </span>
              ))}
            </div>
          </td>

          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/add-edit-recipe", {
                        state: { nutrition: item, mode: "edit" },
                      })
                    }
                  >
                    View/Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setRecipeDelete(item?._id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Nutrition Recipes
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbParent2={"Nutrition"}
            breadCrumbActive={"Recipes"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
        {/* Search and Export Section */}
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase font-figtree text-base w-40 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => navigate("/add-edit-recipe")}
            type="button"
          >
            + Add Recipe
          </button>
        </div>

        <div className="w-full bg-white shadow rounded-lg">
          {allRecipesLoading ? (
            <RippleLoader />
          ) : (
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              loading={false}
              showPagination={true}
              disableNext={!!allRecipesList?.data?.length}
              disablePrev={!!allRecipesList?.data?.length}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              data={allRecipesList?.data}
              total={allRecipesList?.data?.pagination?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(
                allRecipesList?.data?.pagination?.total / pageLimit,
              )}
            />
          )}
        </div>
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteRecipeId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default NutritionRecipes;
